// Domaine Activités : Accueil, Agir, Apprendre, Liberty Coach.
// Composants réels de vendor/ubm-ui-components (imports granulaires) ; données dans activities-fixtures.ts ;
// état local partagé entre pages et sidepeek (aucun réseau, aucun envoi réel).

import { useId, useState, useSyncExternalStore, type DragEvent, type ReactNode } from "react";
import { ArrowLeft, ArrowUpRight, BookOpen, CalendarDays, Check, ChevronLeft, ChevronRight, Clock, Compass, ExternalLink, FileText, GraduationCap, ListTodo, Lock, MapPin, MessageCircle, PlayCircle, Plus, Repeat, SlidersHorizontal, Trophy, UserRound, Video, History as HistoryIcon } from "lucide-react";
import { DatePicker } from "../vendor/ubm-ui-components/src/components/date-picker";
import { Popover, PopoverContent, PopoverTrigger } from "../vendor/ubm-ui-components/src/components/popover";

import { Badge, type BadgeTone } from "../vendor/ubm-ui-components/src/components/badge";
import { Button, ButtonCore } from "../vendor/ubm-ui-components/src/components/button";
import { Card, CardSurface } from "../vendor/ubm-ui-components/src/components/card";
import { Carousel } from "../vendor/ubm-ui-components/src/components/carousel";
import { ChipGroup } from "../vendor/ubm-ui-components/src/components/chip-group";
import { Checkbox } from "../vendor/ubm-ui-components/src/components/checkbox";
import { EmptyState } from "../vendor/ubm-ui-components/src/components/empty-state";
import { Input } from "../vendor/ubm-ui-components/src/components/input";
import { Textarea } from "../vendor/ubm-ui-components/src/components/textarea";
import { Progress } from "../vendor/ubm-ui-components/src/components/progress";
import { SegmentedControl } from "../vendor/ubm-ui-components/src/components/segmented-control";
import { TreeView, type TreeNode } from "../vendor/ubm-ui-components/src/components/tree-view";
import { LineChart, type LineChartDatum, type LineChartSeries } from "../vendor/ubm-ui-components/src/components/line-chart";
import { BarChart, type BarChartDatum } from "../vendor/ubm-ui-components/src/components/bar-chart";
import { ChatThread, type ChatMessage } from "../vendor/ubm-ui-components/src/components/chat-thread";
import { NumberField } from "../vendor/ubm-ui-components/src/components/number-field";
import { Timeline } from "../vendor/ubm-ui-components/src/components/timeline";

import type { PageProps, Route } from "./contracts";
import { Composer, MOBILE_QUERY, useMediaQuery } from "./shared";
import { eventVisual } from "./event-visuals";
import { BilanExperience, useCurrentBilan } from "./exchanges";
import {
  AGENDA_ACCESS_LABEL, AGENDA_EVENTS, AGENDA_SOURCES, ASSET_BASE, DEMO_NOW, DEMO_TODAY, GOAL_RULES,
  INITIAL_ACTIONS, INITIAL_ENTRIES, INITIAL_GOALS, INITIAL_LEARN_PROGRESS, INITIAL_ROUTINES, INITIAL_WEEKS,
  LESSONS, LESSON_COVERS, LEVELS, LIBERTY_CATEGORIES, LIBERTY_LINKS, LIBERTY_PARTICIPANTS, LIBERTY_RESOURCES, MEMBER_FIRST_NAME,
  QUICK_ACCESS, RESOURCES, RESOURCE_COLLECTIONS, STEPS,
  type ActionItem, type Goal, type GoalEntry, type GoalWeek, type LearnProgress, type Lesson, type LibertyCategory, type LibertyLink,
  type Resource, type ResourceCollection, type Routine, type RoutineFreq,
} from "./activities-fixtures";
import "./activities.css";

type OpenPeek = PageProps["openPeek"];
type Navigate = PageProps["navigate"];

// ── État local partagé (pages + contenu du sidepeek) ─────────────────────────

interface ActivitiesState {
  weeks: GoalWeek[];
  goals: Record<string, Goal>;
  entries: GoalEntry[];
  routines: Routine[];
  /** Routines cochées aujourd’hui. */
  routineToday: Record<string, boolean>;
  actions: ActionItem[];
  actionDone: Record<string, boolean>;
  /** Planning override of an action's day, set by drag and drop or the date picker: an ISO day, or null = « Sans date ».
   *  It prevails over the fixture `due` and over the time inferred from the meta (consumer model, memory only). */
  actionDue: Record<string, string | null>;
  learn: LearnProgress;
  libraryColl: "all" | ResourceCollection;
  libraryThemes: string[];
  libertyMessages: ChatMessage[];
  libertyPoint: { focus: string; results: string; question: string } | null;
  libertyTrackingOpened: boolean;
  /** Horloge de démonstration en minutes (18 h 30), avance d’une minute par geste. */
  clock: number;
  seq: number;
}

let state: ActivitiesState = {
  weeks: INITIAL_WEEKS,
  goals: INITIAL_GOALS,
  entries: INITIAL_ENTRIES,
  routines: INITIAL_ROUTINES,
  routineToday: {},
  actions: INITIAL_ACTIONS,
  actionDone: {},
  actionDue: {},
  learn: INITIAL_LEARN_PROGRESS,
  libraryColl: "all",
  libraryThemes: [],
  libertyMessages: [],
  libertyPoint: null,
  libertyTrackingOpened: false,
  clock: 18 * 60 + 30,
  seq: 100,
};
const listeners = new Set<() => void>();
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const update = (fn: (s: ActivitiesState) => ActivitiesState) => { state = fn(state); listeners.forEach(l => l()); };
export const useActivities = () => useSyncExternalStore(subscribe, () => state, () => state);
const nextId = (prefix: string) => { const id = prefix + state.seq; update(s => ({ ...s, seq: s.seq + 1 })); return id; };
const tick = () => { const t = state.clock; update(s => ({ ...s, clock: s.clock + 1 })); return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; };

// ── Dates et mesures ─────────────────────────────────────────────────────────

const parseDay = (iso: string) => { const [y, m, d] = iso.split("-").map(Number) as [number, number, number]; return new Date(y, m - 1, d); };
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (iso: string, n: number) => { const d = parseDay(iso); d.setDate(d.getDate() + n); return isoDay(d); };
const fmtShort = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });
const fmtLong = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const fmtWeekday = new Intl.DateTimeFormat("fr-FR", { weekday: "short" });
const weekLabel = (start: string) => `Semaine du ${fmtShort.format(parseDay(start))}`;
const dayLabel = (iso: string) => fmtLong.format(parseDay(iso));
const timeLabel = (hhmm: string) => hhmm.replace(":", " h ");
const todayWeekday = parseDay(DEMO_TODAY).getDay();
/** Création d’un objectif de la semaine : du lundi au mercredi (règle de la reprise). */
const weekCreationOpen = todayWeekday >= 1 && todayWeekday <= 3;

const currentWeek = (s: ActivitiesState) => s.weeks.find(w => !w.closed) ?? s.weeks[0]!;
const entriesOf = (s: ActivitiesState, goalId: string) => s.entries.filter(e => e.goal === goalId);
const unitOf = (g: Goal, n: number) => (n > 1 ? g.many : g.one) ?? "";

interface Measure { value: number; target?: number; pct: number; big: string; small: string; reached: boolean }
function measureOf(s: ActivitiesState, g: Goal): Measure {
  const list = entriesOf(s, g.id);
  if (g.kind === "binary") {
    const done = list.filter(e => e.mode === "binary").at(-1)?.v === 1 || (list.every(e => e.mode !== "binary") && !!g.done);
    return { value: done ? 1 : 0, target: 1, pct: done ? 100 : 0, big: done ? "Fait" : "À faire", small: "", reached: done };
  }
  if (g.target === undefined) {
    const last = list.filter(e => e.mode === "reading").at(-1);
    const v = last?.v ?? 0;
    return { value: v, pct: 0, big: String(v), small: ` ${unitOf(g, v)} · relevé`, reached: false };
  }
  const v = list.reduce((sum, e) => sum + (e.mode === "add" ? e.v : 0), 0);
  return { value: v, target: g.target, pct: Math.min(100, Math.round((v / g.target) * 100)), big: String(v), small: ` / ${g.target} ${unitOf(g, g.target)}`, reached: v >= g.target };
}

const routineDoneCount = (s: ActivitiesState, r: Routine) => r.past.length + (s.routineToday[r.id] ? 1 : 0);
const routineDueToday = (r: Routine) => r.days === null || r.days.includes(todayWeekday);

// ── Mutations ────────────────────────────────────────────────────────────────

function addEntry(goalId: string, v: number, mode: GoalEntry["mode"]) {
  const time = tick(), id = nextId("e");
  update(s => ({ ...s, entries: [...s.entries, { id, goal: goalId, day: DEMO_TODAY, time, v, mode }] }));
}
const toggleRoutine = (id: string, done: boolean) => update(s => ({ ...s, routineToday: { ...s.routineToday, [id]: done } }));
const toggleAction = (id: string, done: boolean) => update(s => ({ ...s, actionDone: { ...s.actionDone, [id]: done } }));
/** Planning day of an action: explicit override first (null = Sans date), then the fixture `due`, then a time slot in the
 *  meta (« 14 h 00–14 h 30 ») which means today. */
const effectiveDue = (s: ActivitiesState, a: ActionItem): string | undefined => {
  if (a.id in s.actionDue) return s.actionDue[a.id] ?? undefined;
  return a.due ?? (actionTime(a.meta) ? DEMO_TODAY : undefined);
};
const setActionDue = (id: string, day: string | null) => {
  if (!state.actions.some(a => a.id === id)) return;
  if (day !== null && (!/^\d{4}-\d{2}-\d{2}$/.test(day) || isoDay(parseDay(day)) !== day)) return;
  update(s => ({ ...s, actionDue: { ...s.actionDue, [id]: day } }));
};
const isActionLate = (s: ActivitiesState, a: ActionItem) => {
  const due = effectiveDue(s, a);
  return !s.actionDone[a.id] && (due ? due < DEMO_TODAY : !!a.late);
};
export const markLesson = (id: string, done: boolean) => update(s => {
  const next = { ...s.learn.done };
  if (done) next[id] = DEMO_TODAY; else delete next[id];
  return { ...s, learn: { ...s.learn, done: next, last: id } };
});
/** Reading position of a lesson (seconds, 0 accepted on opening) and last lesson opened. Unchanged values keep the same
 *  state object and notify nobody, so a caller may report the current position on every open without a render loop. */
export function setLessonPosition(id: string, seconds: number) {
  const pos = Math.max(0, Math.round(seconds));
  if (state.learn.last === id && state.learn.pos[id] === pos) return;
  update(s => ({ ...s, learn: { ...s.learn, pos: { ...s.learn.pos, [id]: pos }, last: id } }));
}

// ── Petits assemblages de domaine (sur composants vendor) ────────────────────

function Section({ title, aside, children, className }: { title: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={["act-section", className].filter(Boolean).join(" ")}>
      <header className="act-section-head"><h2>{title}</h2>{aside}</header>
      {children}
    </section>
  );
}

function Notice({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "danger" | "success" }) {
  return <p className="act-notice" data-tone={tone} role={tone === "neutral" ? undefined : "status"}>{children}</p>;
}

// ── Objectifs : saisie, détail, carte ────────────────────────────────────────

function EntryForm({ goalId, compact = false }: { goalId: string; compact?: boolean }) {
  const s = useActivities();
  const g = s.goals[goalId];
  const initial = g ? measureOf(s, g).value : 0;
  const [value, setValue] = useState(initial);
  const [saved, setSaved] = useState(false);
  if (!g) return null;
  const week = g.week ? s.weeks.find(w => w.key === g.week) : undefined;
  if (week?.closed) return null;
  const m = measureOf(s, g);
  if (g.kind === "binary") return <Checkbox label="Objectif atteint" checked={m.reached}
    onCheckedChange={c => addEntry(g.id, c === true ? 1 : 0, "binary")} />;
  return <form className="act-goal-edit" onSubmit={e => { e.preventDefault(); if (value === m.value) return;
    addEntry(g.id, g.target === undefined ? value : value - m.value, g.target === undefined ? "reading" : "add"); setSaved(true); }}>
    <NumberField label="Résultat actuel" value={value} onValueChange={v => { setValue(v); setSaved(false); }} min={0}
      suffix={` ${unitOf(g, value)}`} size="lg" locale="fr-FR" />
    <Button type="submit" disabled={value === m.value}>{saved ? "Enregistré" : "Enregistrer"}</Button>
  </form>;
}

function GoalDetail({ goalId, navigate, openPeek, inlineEntry = false }: { goalId: string; navigate: Navigate; openPeek: OpenPeek; inlineEntry?: boolean }) {
  const s = useActivities();
  const [addingAction, setAddingAction] = useState(false);
  const g = s.goals[goalId];
  if (!g) return null;
  const m = measureOf(s, g);
  const week = g.week ? s.weeks.find(w => w.key === g.week) : undefined;
  const list = entriesOf(s, g.id).slice().reverse();
  // Contextual links: real titles only (placeholders like « [Titre de la leçon] » point nowhere and are not shown).
  const links = (g.links ?? []).filter(l => l.kind !== "Action" && !/^\[.*\]$/.test(l.title.trim()));
  const related = s.actions.filter(a => a.goal === g.id || (!a.goal && g.links?.some(l => l.kind === "Action" && l.title === a.title)));
  const linkActions = (ids: string[]) => update(st => ({
    ...st,
    actions: st.actions.map(a => ids.includes(a.id) ? { ...a, goal: g.id } : a.goal === g.id ? { ...a, goal: undefined } : a),
    goals: { ...st.goals, [g.id]: { ...st.goals[g.id]!, links: [
      ...(st.goals[g.id]!.links ?? []).filter(l => l.kind !== "Action"),
      ...st.actions.filter(a => ids.includes(a.id)).map(a => ({ kind: "Action" as const, title: a.title })),
    ] } },
  }));
  const linkIcon = (kind: string) => kind === "Leçon" ? <GraduationCap {...META_ICON} /> : kind === "Ressource" ? <BookOpen {...META_ICON} /> : <ListTodo {...META_ICON} />;
  return (
    <div className="act-peek act-goal-detail">
      <section className="act-goal-part" aria-labelledby={`act-gd-title-${g.id}`}>
        <p className="act-peek-kicker act-goal-kicker"><Trophy {...META_ICON} />{week ? weekLabel(week.start) : "En continu"}{week?.closed ? " · clôturée" : ""}</p>
        <div className="act-goal-progress-head">
          <h3 className="act-goal-part-title" id={`act-gd-title-${g.id}`}>{g.title}</h3>
          <span className="act-measure"><b>{m.big}</b><span>{m.small}</span></span>
        </div>
        {m.target !== undefined && <Progress value={m.pct} max={100} aria-labelledby={`act-gd-title-${g.id}`} />}
      </section>

      {inlineEntry && <section className="act-goal-part"><EntryForm goalId={g.id} compact /></section>}
      <section className="act-goal-part">
        <h3 className="act-peek-h">Historique</h3>
        {list.length === 0 ? <p className="act-muted">Aucun relevé pour l’instant.</p> : <Timeline
          className="act-goal-timeline" label={`Historique de ${g.title}`} now={DEMO_NOW.getTime()} locale="fr-FR" timeZone="Europe/Paris" headingLevel={4}
          events={list.map(e => ({ id: e.id, at: `${e.day}T${e.time}:00+02:00`,
            title: e.mode === "binary" ? (e.v ? "Objectif atteint" : "Objectif rouvert") : e.mode === "reading" ? `${e.v} ${unitOf(g, e.v)}` : `${e.v > 0 ? "+" : ""}${e.v} ${unitOf(g, Math.abs(e.v))}`,
            meta: `${dayLabel(e.day)} · ${timeLabel(e.time)}`,
            icon: <Trophy {...META_ICON} /> }))} />}
      </section>

      <section className="act-goal-part">
        <div className="act-section-head"><h3 className="act-peek-h">Pour avancer</h3>
          <Button variant="ghost" size="sm" leading={<Plus size={16} />} onClick={() => setAddingAction(v => !v)}>{addingAction ? "Fermer" : "Ajouter une action"}</Button>
        </div>
        {addingAction && <ActionForm initialGoalId={g.id} />}
        {related.length > 0 && <CompactRows label="Actions liées à cet objectif" rows={related.map(a => ({ id: a.id, title: a.title, done: !!s.actionDone[a.id],
          toggle: done => toggleAction(a.id, done), open: () => openPeek({ title: a.title, content: <ActionDetail id={a.id} navigate={navigate} />, footer: <ActionFooter id={a.id} /> }) }))} />}
        {s.actions.length > 0 && <details className="act-related-picker"><summary>Lier une action existante</summary>
          <ChipGroup label="Actions liées" options={s.actions.map(a => ({ value: a.id, label: a.title }))} value={related.map(a => a.id)} onValueChange={linkActions} />
        </details>}
      </section>

      {links.length > 0 && <MetaList items={links.map(l => ({
        icon: linkIcon(l.kind), label: l.kind, value: l.title,
        action: l.kind === "Leçon" || l.kind === "Ressource" ? <Button variant="ghost" size="sm" onClick={() => navigate("learn")}>Apprendre</Button> : undefined,
      }))} />}
    </div>
  );
}

/** Entry for the goal, pinned in the shared peek footer (PeekDetail.footer). Same store mutations and validations as EntryForm. */
function GoalEntryFooter({ goalId }: { goalId: string }) {
  return <div className="act-goal-entry"><EntryForm goalId={goalId} compact /></div>;
}

const openGoal = (openPeek: OpenPeek, navigate: Navigate, g: Goal) =>
  openPeek({ title: g.title, content: <GoalDetail goalId={g.id} navigate={navigate} openPeek={openPeek} />, footer: <GoalEntryFooter goalId={g.id} /> });

/**
 * Shared goal card (Accueil, Agir, Mon suivi): the whole card is one ButtonCore that opens the shared GoalDetail peek
 * (entry form in its footer). Optional greeting line on the Accueil only; the arrow is decorative (no nested button).
 */
function GoalCard({ g, openPeek, navigate, hello }: { g: Goal; openPeek: OpenPeek; navigate: Navigate; hello?: string }) {
  const s = useActivities();
  const m = measureOf(s, g);
  const mobile = useMediaQuery(MOBILE_QUERY);
  const [open, setOpen] = useState(false);
  const card = (
    <ButtonCore variant="ghost" accent={false} morph={false} className="act-premium act-premium-card" data-theme="dark" data-reached={m.reached || undefined}
      data-hello={hello ? true : undefined} aria-label={`${g.title}, ${m.big} ${m.small}. Ouvrir l’objectif`} onClick={() => openGoal(openPeek, navigate, g)}>
      <span className="act-premium-top"><Trophy size={16} strokeWidth={1.75} aria-hidden className="act-goal-icon" />{hello && <span className="act-premium-hello">{hello}</span>}</span>
      <span className="act-premium-head">
        <span className="act-premium-title" id={`act-goal-title-${g.id}`}>{g.title}</span>
        <span className="act-premium-measure"><b>{m.big}</b><span>{m.small}</span></span>
      </span>
      {/* Accessible name only (no visible label): "Progression <titre>" via aria-labelledby, which wins over the source aria-label. */}
      {m.target !== undefined && <>
        <Progress value={m.pct} max={100} aria-labelledby={`act-goal-title-${g.id}`} tone={m.reached ? "success" : "accent"} />
      </>}
      <span className="act-premium-cta" aria-hidden><ArrowUpRight size={18} /></span>
    </ButtonCore>
  );
  return card;
}

// ── Tâches et routines du jour (Accueil et Agir) ─────────────────────────────

/** Footer button of the action/routine detail, passed as PeekDetail.footer: one real Button bound to the store toggle (pressed = done; press again to undo). */
function DetailFooter({ done, todo, doneLabel, onToggle }: { done: boolean; todo: string; doneLabel: string; onToggle: (next: boolean) => void }) {
  return (
    <Button variant={done ? "secondary" : "primary"} aria-pressed={done} leading={done ? <Check size={16} aria-hidden /> : undefined} onClick={() => onToggle(!done)}>
      {done ? doneLabel : todo}
    </Button>
  );
}
/** Detail metadata of the side peeks (action, routine, event): lucide icon + muted label + value, no row separators. */
const META_ICON = { size: 16, strokeWidth: 1.75, "aria-hidden": true } as const;
type MetaItem = { icon: ReactNode; label: string; value: ReactNode; action?: ReactNode };
function MetaList({ items }: { items: (MetaItem | false | undefined | null | "")[] }) {
  const shown = items.filter((i): i is MetaItem => !!i);
  if (!shown.length) return null;
  return (
    <dl className="act-meta">
      {shown.map(i => (
        <div key={`${i.label}-${typeof i.value === "string" ? i.value : ""}`} className="act-meta-item">
          <span className="act-meta-icon">{i.icon}</span>
          <div className="act-meta-text"><dt>{i.label}</dt><dd>{i.value}</dd></div>
          {i.action}
        </div>
      ))}
    </dl>
  );
}

/** Live footers: they subscribe to the store themselves, so the pinned bar always shows the current state. */
function RoutineFooter({ id }: { id: string }) {
  const s = useActivities();
  return <DetailFooter done={!!s.routineToday[id]} todo="Cocher pour aujourd’hui" doneLabel="Fait aujourd’hui" onToggle={next => toggleRoutine(id, next)} />;
}
function ActionFooter({ id }: { id: string }) {
  const s = useActivities();
  return <DetailFooter done={!!s.actionDone[id]} todo="Marquer comme terminé" doneLabel="Terminé" onToggle={next => toggleAction(id, next)} />;
}

function RoutineDetail({ id }: { id: string }) {
  const s = useActivities();
  const r = s.routines.find(x => x.id === id);
  if (!r) return null;
  const done = routineDoneCount(s, r);
  const goal = r.goal ? s.goals[r.goal] : undefined;
  return (
    <div className="act-peek">
      <p className="act-peek-kicker">Routine · {r.rhythm}</p>
      <div className="act-measure"><b>{done}</b><span> / {r.n} cette semaine</span></div>
      <Progress value={Math.min(done, r.n)} max={r.n} label="Réalisé cette semaine" />
      <MetaList items={[
        { icon: <CalendarDays {...META_ICON} />, label: "Jours prévus", value: routineDueToday(r) ? `Prévue aujourd’hui · ${r.short}` : r.short },
        { icon: <Repeat {...META_ICON} />, label: "Origine", value: r.origin },
        goal && { icon: <Trophy {...META_ICON} />, label: "Accompagne l’objectif", value: goal.title },
      ]} />
    </div>
  );
}

function ActionDetail({ id, navigate }: { id: string; navigate: Navigate }) {
  const s = useActivities();
  const a = s.actions.find(x => x.id === id);
  if (!a) return null;
  const goal = a.goal ? s.goals[a.goal] : undefined;
  return (
    <div className="act-peek">
      <p className="act-peek-kicker">Action{isActionLate(s, a) ? " · en retard" : ""}</p>
      <p className="act-text">{a.meta}</p>
      <ActionDuePicker a={a} />
      <MetaList items={[
        a.origin && { icon: <Compass {...META_ICON} />, label: "Origine", value: a.origin },
        goal && { icon: <Trophy {...META_ICON} />, label: "Objectif lié", value: goal.title,
          action: <Button variant="ghost" size="sm" onClick={() => navigate("agir")}>Voir</Button> },
      ]} />
    </div>
  );
}

/** Vendored DatePicker bound to the planning override: a picked day moves the action, « Retirer la date » puts it in Sans date. */
function ActionDuePicker({ a }: { a: ActionItem }) {
  const s = useActivities();
  const due = effectiveDue(s, a);
  return (
    <div className="act-due-picker">
      <DatePicker label="Date prévue" value={due ? parseDay(due) : undefined} onChange={d => setActionDue(a.id, d ? isoDay(d) : null)}
        placeholder="Sans date" locale="fr-FR" format={{ weekday: "long", day: "numeric", month: "long" }} weekStartsOn={1} showToday />
      {due && <Button variant="ghost" size="sm" onClick={() => setActionDue(a.id, null)}>Retirer la date</Button>}
    </div>
  );
}

/** A scheduled time carried by an action's meta (e.g. « 14 h 00–14 h 30 »), shown alone on the compact row. */
const actionTime = (meta: string) => meta.match(/\d{1,2} h \d{2}(?:–\d{1,2} h \d{2})?/)?.[0];

/**
 * Shared list of actions and routines (Accueil and Agir): one compact row = Checkbox + title (+ time when there is one).
 * Details and their toggles stay in RoutineDetail / ActionDetail. `limit` shows the first rows and discloses the rest.
 */
/** `toggle` absent = read-only row (no checkbox): e.g. a routine on a past or future day of the planning. */
type ListRow = { id: string; title: string; time?: string; done: boolean; toggle?: (c: boolean) => void; open: () => void };
// Timed items first (by time), then untimed ones in their existing order; sort is stable.
const rowMinutes = (time?: string) => { const m = time?.match(/(\d{1,2}) h (\d{2})/); return m ? Number(m[1]) * 60 + Number(m[2]) : Infinity; };
const byTime = (rows: ListRow[]) => rows.slice().sort((a, b) => rowMinutes(a.time) - rowMinutes(b.time));

function CompactRows({ rows, id, label }: { rows: ListRow[]; id?: string; label: string }) {
  return (
    <ul id={id} className="act-compact-rows" aria-label={label}>
      {rows.map(r => (
        <li key={r.id} className="act-compact-row" data-done={r.done || undefined}>
          {r.toggle && <Checkbox aria-label={`${r.title} : faite`} checked={r.done} onCheckedChange={c => r.toggle?.(c === true)} />}
          <ButtonCore variant="ghost" accent={false} morph={false} className="act-compact-open" onClick={r.open}>
            <span className="act-compact-title">{r.title}</span>
            {r.time && <span className="act-compact-time">{r.time}</span>}
          </ButtonCore>
        </li>
      ))}
    </ul>
  );
}

/**
 * Shared list of actions and routines (Accueil and Agir): one compact row = Checkbox + title (+ time right after it).
 * Accueil: routines of the day + actions, `limit` first rows then disclosure. Agir (`split`): « Aujourd’hui » = routines due
 * today + actions scheduled today, due on or before today, or late (unfinished ones stay); « Voir toutes mes actions »
 * discloses, on the same page, À venir / Sans date / the other routines.
 */
function ActionList({ openPeek, navigate, split = false, limit }: { openPeek: OpenPeek; navigate: Navigate; split?: boolean; limit?: number }) {
  const s = useActivities();
  const listId = useId();
  const [expanded, setExpanded] = useState(false);
  const routineRow = (r: Routine): ListRow => ({ id: r.id, title: r.title, done: !!s.routineToday[r.id],
    toggle: c => toggleRoutine(r.id, c), open: () => openPeek({ title: r.title, content: <RoutineDetail id={r.id} />, footer: <RoutineFooter id={r.id} /> }) });
  const actionRow = (a: ActionItem, time = actionTime(a.meta)): ListRow => ({ id: a.id, title: a.title, time, done: !!s.actionDone[a.id],
    toggle: c => toggleAction(a.id, c), open: () => openPeek({ title: a.title, content: <ActionDetail id={a.id} navigate={navigate} />, footer: <ActionFooter id={a.id} /> }) });

  if (split) {
    const isToday = (a: ActionItem) => !!a.late || (a.due !== undefined && a.due <= DEMO_TODAY) || (a.due === undefined && !!actionTime(a.meta));
    const today = byTime([...s.routines.filter(routineDueToday).map(r => routineRow(r)), ...s.actions.filter(isToday).map(a => actionRow(a, a.late ? "En retard" : actionTime(a.meta)))]);
    const upcoming = s.actions.filter(a => !isToday(a) && a.due !== undefined).sort((a, b) => a.due!.localeCompare(b.due!)).map(a => actionRow(a, fmtShort.format(parseDay(a.due!))));
    const undated = s.actions.filter(a => !isToday(a) && a.due === undefined).map(a => actionRow(a));
    const others = s.routines.filter(r => !routineDueToday(r)).map(r => ({ ...routineRow(r), time: r.short }));
    const rest = [{ key: "up", label: "À venir", rows: upcoming }, { key: "nd", label: "Sans date", rows: undated }, { key: "ro", label: "Autres routines", rows: others }].filter(g => g.rows.length);
    return (
      <div className="act-actions-list">
        {today.length ? <CompactRows rows={today} label="Aujourd’hui" /> : <p className="act-muted">Rien de prévu aujourd’hui.</p>}
        {rest.length > 0 && <>
          <Button variant="ghost" size="sm" aria-expanded={expanded} aria-controls={listId} onClick={() => setExpanded(v => !v)}>
            {expanded ? "Masquer les autres actions" : "Voir toutes mes actions"}
          </Button>
          <div id={listId} className="act-action-groups" hidden={!expanded}>
            {rest.map(g => (
              <div key={g.key} className="act-action-group">
                <h3 className="act-action-group-h">{g.label}</h3>
                <CompactRows rows={g.rows} label={g.label} />
              </div>
            ))}
          </div>
        </>}
      </div>
    );
  }

  const rows = byTime([...s.routines.filter(routineDueToday).map(r => routineRow(r)), ...s.actions.map(a => actionRow(a))]);
  const hidden = limit !== undefined && !expanded ? Math.max(0, rows.length - limit) : 0;
  const shown = hidden ? rows.slice(0, limit) : rows;
  return (
    <div className="act-actions-list">
      <CompactRows rows={shown} id={listId} label="Actions et routines" />
      {limit !== undefined && rows.length > limit && (
        <Button variant="ghost" size="sm" aria-expanded={expanded} aria-controls={listId} onClick={() => setExpanded(v => !v)}>
          {expanded ? "Afficher moins" : `Afficher ${rows.length - limit} autre${rows.length - limit > 1 ? "s" : ""}`}
        </Button>
      )}
    </div>
  );
}

// ── Ajout : action, routine, objectif ────────────────────────────────────────

type AddKind = "action" | "routine" | "goal";

function GoalLinkPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const s = useActivities();
  const week = currentWeek(s);
  const ids = [...week.goals, ...Object.values(s.goals).filter(g => !g.week).map(g => g.id)];
  return <ChipGroup label="Objectif lié (facultatif)" options={ids.map(id => ({ value: id, label: s.goals[id]?.title ?? id }))} value={value ? [value] : []} onValueChange={v => onChange(v.at(-1) ?? "")} />;
}

function ActionForm({ initialGoalId = "" }: { initialGoalId?: string }) {
  const [title, setTitle] = useState(""), [due, setDue] = useState(""), [goal, setGoal] = useState(initialGoalId), [ack, setAck] = useState("");
  const ok = title.trim().length > 0 && (due === "" || due >= DEMO_TODAY);
  return (
    <form className="act-form" onSubmit={e => {
      e.preventDefault(); if (!ok) return;
      const id = nextId("ua"), t = title.trim();
      const action: ActionItem = { id, title: t, meta: due ? `Échéance ${fmtShort.format(parseDay(due))} · Initiative` : "Sans date · Initiative", origin: "Initiative", ...(due ? { due } : {}), ...(goal ? { goal } : {}) };
      update(s => ({ ...s, actions: [...s.actions, action] }));
      setAck(`Action ajoutée : ${t}`); setTitle(""); setDue(""); setGoal(initialGoalId);
    }}>
      <Input label="Intitulé" value={title} onChange={e => { setTitle(e.target.value); setAck(""); }} placeholder="Ce que tu vas faire" />
      <DatePicker label="Date prévue" value={due ? parseDay(due) : undefined} minDate={parseDay(DEMO_TODAY)} onChange={d => setDue(d ? isoDay(d) : "")}
        placeholder="Sans date" locale="fr-FR" weekStartsOn={1} showToday />
      <GoalLinkPicker value={goal} onChange={setGoal} />
      <Button type="submit" disabled={!ok}>Ajouter l’action</Button>
      {ack && <Notice tone="success">{ack}</Notice>}
    </form>
  );
}

const FREQS: { value: RoutineFreq; label: string }[] = [{ value: "daily", label: "Tous les jours" }, { value: "workdays", label: "Jours ouvrés" }, { value: "weekly", label: "Par semaine" }];

function RoutineForm() {
  const [title, setTitle] = useState(""), [freq, setFreq] = useState<RoutineFreq>("daily"), [qty, setQty] = useState("1"), [times, setTimes] = useState(""), [goal, setGoal] = useState(""), [ack, setAck] = useState("");
  const q = Number(qty), n = Number(times);
  const okQ = Number.isInteger(q) && q >= 1, okN = freq !== "weekly" || (Number.isInteger(n) && n >= 1 && n <= 7);
  const ok = title.trim().length > 0 && okQ && okN;
  return (
    <form className="act-form" onSubmit={e => {
      e.preventDefault(); if (!ok) return;
      const id = nextId("ur"), t = title.trim();
      const per = freq === "weekly" ? "par fois" : "par jour";
      const rhythm = `${freq === "daily" ? "Tous les jours" : freq === "workdays" ? "Jours ouvrés" : `${n} fois par semaine`} · ${q} ${per}`;
      const routine: Routine = { id, title: t, rhythm, short: freq === "daily" ? "tous les jours" : freq === "workdays" ? "jours ouvrés" : `${n} × / sem.`,
        n: freq === "daily" ? 7 : freq === "workdays" ? 5 : n, days: freq === "daily" ? [0, 1, 2, 3, 4, 5, 6] : freq === "workdays" ? [1, 2, 3, 4, 5] : null, past: [], origin: "Choisie par toi", ...(goal ? { goal } : {}) };
      update(s => ({ ...s, routines: [...s.routines, routine] }));
      setAck(`Routine créée : ${t}`); setTitle(""); setTimes(""); setQty("1"); setGoal("");
    }}>
      <Input label="Intitulé" value={title} onChange={e => { setTitle(e.target.value); setAck(""); }} placeholder="Ce que tu répètes" />
      <SegmentedControl label="Fréquence" options={FREQS} value={freq} onValueChange={v => setFreq(v as RoutineFreq)} />
      {freq === "weekly" && <Input label="Nombre de fois par semaine" type="number" inputMode="numeric" min={1} max={7} value={times} onChange={e => setTimes(e.target.value)}
        error={times !== "" && !okN ? "Entre 1 et 7 fois." : undefined} />}
      <Input label={`Quantité ${freq === "weekly" ? "par fois" : "par jour"}`} type="number" inputMode="numeric" min={1} value={qty} onChange={e => setQty(e.target.value)}
        error={qty !== "" && !okQ ? "Saisis un nombre entier à partir de 1." : undefined} />
      <GoalLinkPicker value={goal} onChange={setGoal} />
      <Button type="submit" disabled={!ok}>Créer la routine</Button>
      {ack && <Notice tone="success">{ack}</Notice>}
    </form>
  );
}

function GoalForm() {
  const s = useActivities();
  const week = currentWeek(s);
  const full = week.goals.length >= GOAL_RULES.maxGoals;
  const [period, setPeriod] = useState<"week" | "continuous">(weekCreationOpen && !full ? "week" : "continuous");
  const [kind, setKind] = useState<"target" | "binary">("target");
  const [title, setTitle] = useState(""), [result, setResult] = useState(""), [target, setTarget] = useState(""), [unit, setUnit] = useState(""), [ack, setAck] = useState("");
  const t = Number(target);
  const weekRefused = period === "week" && (!weekCreationOpen || full);
  const ok = !weekRefused && title.trim() !== "" && result.trim() !== "" && (kind === "binary" || (Number.isInteger(t) && t >= 1 && unit.trim() !== ""));
  const refusal = !weekCreationOpen ? `Les objectifs de la semaine se créent jusqu’au ${GOAL_RULES.lateCreationUntil}.` : full ? `Cette semaine compte déjà ${GOAL_RULES.maxGoals} objectifs.` : "";
  return (
    <form className="act-form" onSubmit={e => {
      e.preventDefault(); if (!ok) return;
      const id = nextId("n"), u = unit.trim();
      const g: Goal = { id, kind, title: title.trim(), result: result.trim(), created: DEMO_TODAY, ...(period === "week" ? { week: week.key } : {}),
        ...(kind === "target" ? { target: t, many: u, one: u.length > 3 && /s$/.test(u) ? u.slice(0, -1) : u } : { done: false }) };
      update(st => ({ ...st, goals: { ...st.goals, [id]: g }, weeks: period === "week" ? st.weeks.map(w => (w.key === week.key ? { ...w, goals: [...w.goals, id] } : w)) : st.weeks }));
      setAck(`Objectif créé : ${g.title}`); setTitle(""); setResult(""); setTarget(""); setUnit("");
    }}>
      <SegmentedControl label="Période" options={[{ value: "week", label: "Cette semaine" }, { value: "continuous", label: "En continu" }]} value={period} onValueChange={v => setPeriod(v as "week" | "continuous")} />
      {weekRefused ? <Notice tone="danger">{refusal} Choisis « En continu » ou attends lundi.</Notice>
        : <p className="act-muted">{period === "week" ? `${weekLabel(week.start)} · création possible jusqu’au ${GOAL_RULES.lateCreationUntil}.` : "Sans fin de semaine : il se poursuit d’une semaine à l’autre."}</p>}
      <Input label="Intitulé" value={title} onChange={e => { setTitle(e.target.value); setAck(""); }} placeholder="Par exemple : Abonnements small group" />
      <Textarea label="Résultat visé" rows={2} value={result} onChange={e => setResult(e.target.value)} placeholder="Ce que tu veux obtenir, en une phrase" />
      <SegmentedControl label="Mesure" options={[{ value: "target", label: "Cible chiffrée" }, { value: "binary", label: "Case à cocher" }]} value={kind} onValueChange={v => setKind(v as "target" | "binary")} />
      {kind === "target" && (
        <div className="act-form-pair">
          <Input label="Cible" type="number" inputMode="numeric" min={1} value={target} onChange={e => setTarget(e.target.value)} error={target !== "" && !(Number.isInteger(t) && t >= 1) ? "Nombre entier à partir de 1." : undefined} />
          <Input label="Unité (au pluriel)" value={unit} onChange={e => setUnit(e.target.value)} placeholder="abonnements" />
        </div>
      )}
      <Button type="submit" disabled={!ok} leading={<Trophy size={16} aria-hidden />}>Créer l’objectif</Button>
      {ack && <Notice tone="success">{ack}</Notice>}
    </form>
  );
}

function AddPanel({ initial }: { initial: AddKind }) {
  const [kind, setKind] = useState<AddKind>(initial);
  return (
    <div className="act-peek">
      <SegmentedControl label="Que veux-tu ajouter ?" options={[{ value: "action", label: "Action" }, { value: "routine", label: "Routine" }, { value: "goal", label: "Objectif" }]}
        value={kind} onValueChange={v => setKind(v as AddKind)} />
      {kind === "action" ? <ActionForm key="a" /> : kind === "routine" ? <RoutineForm key="r" /> : <GoalForm key="g" />}
    </div>
  );
}
const openAdd = (openPeek: OpenPeek, initial: AddKind = "action") => openPeek({ title: "Ajouter", content: <AddPanel initial={initial} /> });

// ── Accueil ──────────────────────────────────────────────────────────────────

function WeeklyGoalsRail({ openPeek, navigate, home = false }: { openPeek: OpenPeek; navigate: Navigate; home?: boolean }) {
  const s = useActivities();
  const week = currentWeek(s);
  const goals = week.goals.map(id => s.goals[id]).filter((g): g is Goal => !!g);
  if (goals.length === 0) {
    return <EmptyState icon={false} title="Quel est ton cap cette semaine ?" action={<Button onClick={() => openAdd(openPeek, "goal")}>Écrire mes objectifs</Button>} />;
  }
  // Same swipe rail and same shared card on Accueil and Agir; only the Accueil card carries the greeting (Agir shows its week once, in its header).
  const hello = home ? `Bonjour ${MEMBER_FIRST_NAME} · tes objectifs de la semaine du ${fmtShort.format(parseDay(week.start))}` : undefined;
  return (
    <Carousel label="Objectifs de la semaine" slideSize="calc(100cqw - 36px)" slideLabel={(i, n) => `Objectif ${i + 1} sur ${n}`} className="act-goal-rail act-goal-rail-home">
      {goals.map(g => <GoalCard key={g.id} g={g} openPeek={openPeek} navigate={navigate} hello={hello} />)}
    </Carousel>
  );
}

function EventDetail({ id, navigate }: { id: string; navigate: Navigate }) {
  const e = AGENDA_EVENTS.find(x => x.id === id);
  if (!e) return null;
  const past = e.date < DEMO_TODAY;
  const target: Partial<Record<Route, string>> = { accompagnement: "Ouvrir dans Coaching", community: "Voir la publication", shop: "Voir l’offre" };
  return (
    <div className="act-peek">
      <img className="act-event-visual" src={eventVisual(e.type)} alt="" />
      <p className="act-peek-kicker">{AGENDA_SOURCES.find(c => c.id === e.type)?.name}{e.access ? ` · ${AGENDA_ACCESS_LABEL[e.access]}` : ""}{past ? " · passé" : ""}</p>
      <MetaList items={[
        { icon: <CalendarDays {...META_ICON} />, label: "Date", value: dayLabel(e.date) },
        { icon: <Clock {...META_ICON} />, label: "Horaire", value: `${timeLabel(e.start)} – ${timeLabel(e.end)} · heure de Paris` },
        { icon: e.place === "site" ? <MapPin {...META_ICON} /> : <Video {...META_ICON} />, label: "Lieu", value: `${e.place === "site" ? e.placeLabel : "En visio"}${e.whereSub ? ` · ${e.whereSub}` : ""}` },
        e.org && { icon: <UserRound {...META_ICON} />, label: "Animé par", value: e.org },
      ]} />
      <p className="act-text">{e.desc}</p>
      <Button variant="secondary" trailing={<ChevronRight size={16} />} onClick={() => navigate(e.route)}>{target[e.route] ?? "Ouvrir"}</Button>
    </div>
  );
}

/**
 * Compact agenda of the Accueil, distinct from the Coaching calendar (whose WeekCalendar and props are untouched):
 * the next three upcoming AGENDA_EVENTS; a row opens the same EventDetail in the peek.
 * Visual = the shared illustration of the event type (event-visuals.ts), never a portrait.
 */
const agendaVisual = (e: (typeof AGENDA_EVENTS)[number]) => eventVisual(e.type);

function HomeAgenda({ openPeek, navigate }: { openPeek: OpenPeek; navigate: Navigate }) {
  const byWhen = (a: (typeof AGENDA_EVENTS)[number], b: (typeof AGENDA_EVENTS)[number]) =>
    (a.date + a.start).localeCompare(b.date + b.start) || a.title.localeCompare(b.title, "fr");
  // The next three events still ahead (start date+time after DEMO_NOW), whatever the week. Past events never appear here.
  const startsAt = (e: (typeof AGENDA_EVENTS)[number]) => { const d = parseDay(e.date); const [h, m] = e.start.split(":").map(Number) as [number, number]; d.setHours(h, m); return d; };
  const rows = AGENDA_EVENTS.filter(e => startsAt(e) > DEMO_NOW).sort(byWhen).slice(0, 3);
  return (
    <div className="act-home-agenda-body">
      {!rows.length && <p className="act-muted">Rien de prévu pour l’instant.</p>}
      {rows.length > 0 && <ul className="act-agenda-rows" aria-label="Prochains rendez-vous">
        {rows.map(e => {
          return (
            <li key={e.id}>
              <ButtonCore variant="ghost" accent={false} morph={false} className="act-agenda-row" onClick={() => openPeek({ title: e.title, content: <EventDetail id={e.id} navigate={navigate} /> })}>
                <img className="act-agenda-thumb" src={agendaVisual(e)} alt="" loading="lazy" />
                <span className="act-agenda-text">
                  <span className="act-agenda-when">{fmtWeekday.format(parseDay(e.date))} {fmtShort.format(parseDay(e.date))} · {timeLabel(e.start)}</span>
                  <span className="act-agenda-title">{e.title}</span>
                  <span className="act-agenda-type">{AGENDA_SOURCES.find(c => c.id === e.type)?.name}</span>
                </span>
              </ButtonCore>
            </li>
          );
        })}
      </ul>}
    </div>
  );
}

/** A section title that is itself the link (one ButtonCore inside the h2, chevron right next to the words; no nested button). */
function SectionLink({ label, hint, onClick }: { label: string; hint: string; onClick: () => void }) {
  return (
    <ButtonCore variant="ghost" accent={false} morph={false} className="act-section-link" aria-label={`${label}, ${hint.charAt(0).toLowerCase()}${hint.slice(1)}`} onClick={onClick}>
      <span>{label}</span><ChevronRight size={18} strokeWidth={1.75} aria-hidden />
    </ButtonCore>
  );
}

function HomePage({ navigate, openPeek }: PageProps) {
  return (
    <div className="act-page act-home">
      <div className="act-home-grid">
        <div className="act-home-main">
          {/* The greeting and date live inside each objective card (WeeklyGoalsRail home), not as a block beside the carousel. */}
          <section className="act-home-hero act-home-week" aria-label="Objectifs de la semaine">
            <WeeklyGoalsRail openPeek={openPeek} navigate={navigate} home />
          </section>
          <Section title={<SectionLink label="Actions du jour" hint="Voir toutes mes actions dans Agir" onClick={() => navigate("agir")} />} className="act-home-todo">
            <ActionList openPeek={openPeek} navigate={navigate} limit={3} />
          </Section>
        </div>
        <Section title={<SectionLink label="Agenda" hint="Voir tout l’agenda dans Coaching" onClick={() => navigate("accompagnement")} />} className="act-home-agenda">
          <HomeAgenda openPeek={openPeek} navigate={navigate} />
        </Section>
      </div>
      <div className="act-home-quick">
        <nav className="act-quick" aria-label="Accès rapides">
          {QUICK_ACCESS.map(q => (
            <CardSurface key={q.id} asChild lift padding="none" radius="lg">
              <ButtonCore variant="ghost" accent={false} morph={false} className="act-quick-tile" data-theme="dark" data-quick={q.id} data-fit={q.fit} aria-label={`${q.title} : ${q.sub}`} onClick={() => navigate(q.route)}>
                <img src={q.image} alt="" loading="lazy" />
              </ButtonCore>
            </CardSurface>
          ))}
        </nav>
      </div>
    </div>
  );
}

// ── Agir ─────────────────────────────────────────────────────────────────────

function HistoryView({ openPeek, navigate }: { openPeek: OpenPeek; navigate: Navigate }) {
  const s = useActivities();
  const [weekKey, setWeekKey] = useState(s.weeks[1]?.key ?? s.weeks[0]!.key);
  const week = s.weeks.find(w => w.key === weekKey) ?? s.weeks[0]!;
  // Charts follow the CHOSEN week: its target goals, its days (bounded by today), its entries.
  const targets = week.goals.map(id => s.goals[id]).filter((g): g is Goal => !!g && g.kind === "target" && g.target !== undefined);
  const days = Array.from({ length: 7 }, (_, i) => addDays(week.start, i)).filter(d => d <= DEMO_TODAY);
  const data: LineChartDatum[] = days.map(d => ({
    key: d, label: dayLabel(d), axisLabel: fmtWeekday.format(parseDay(d)),
    values: Object.fromEntries(targets.map(g => [g.id, Math.round((entriesOf(s, g.id).filter(e => e.day >= week.start && e.day <= d && e.mode === "add").reduce((a, e) => a + e.v, 0) / g.target!) * 100)])),
  }));
  const series: LineChartSeries[] = targets.map(g => ({ key: g.id, label: g.title }));
  // Week-after-week chain of the chosen goal (same title across weeks, reprises included): real measures only.
  const [goalPick, setGoalPick] = useState("");
  const chosen = targets.find(g => g.id === goalPick) ?? targets[0];
  const chain = chosen ? s.weeks.slice().reverse().flatMap(w => w.goals.map(id => s.goals[id]).filter((g): g is Goal => !!g && g.kind === "target" && g.title === chosen.title).map(g => ({ w, g }))) : [];
  const bars: BarChartDatum[] = chain.map(({ w, g }) => ({ key: g.id, label: weekLabel(w.start), axisLabel: fmtShort.format(parseDay(w.start)), value: measureOf(s, g).value }));
  return (
    <div className="act-history">
      <SegmentedControl label="Semaine" options={s.weeks.map(w => ({ value: w.key, label: fmtShort.format(parseDay(w.start)) }))} value={week.key} onValueChange={v => { setWeekKey(v); setGoalPick(""); }} />
      <section className="act-premium act-premium-week" data-theme="dark" aria-label={weekLabel(week.start)}>
        <p className="act-premium-kicker">{weekLabel(week.start)}{week.closed ? " · clôturée" : " · en cours"}</p>
        <ul className="act-premium-list">
          {week.goals.map(id => s.goals[id]).filter((g): g is Goal => !!g).map(g => {
            const m = measureOf(s, g);
            return (
              <li key={g.id}>
                <ButtonCore variant="ghost" accent={false} morph={false} className="act-premium-row" onClick={() => openGoal(openPeek, navigate, g)}>
                  <span className="act-premium-row-title">{g.title}</span>
                  <span className="act-premium-row-value"><b>{m.big}</b>{m.small}</span>
                  <Progress value={m.pct} max={100} label={`Résultat ${g.title}`} tone={m.reached ? "success" : "accent"} />
                </ButtonCore>
              </li>
            );
          })}
        </ul>
      </section>
      <div className="act-charts">
        <CardSurface padding="md" className="act-chart">
          <h3 className="act-peek-h">Progression · {weekLabel(week.start).toLowerCase()}</h3>
          <LineChart label="Progression vers la cible" data={data} series={series} unit="%" height={220} categoryLabel="Jour" formatTick={v => `${v} %`} formatValue={v => `${v} %`} emptyLabel="Aucun relevé cette semaine" />
        </CardSurface>
        {chosen && (
          <CardSurface padding="md" className="act-chart">
            <h3 className="act-peek-h">{chosen.title}, semaine après semaine</h3>
            {targets.length > 1 && <ChipGroup label="Objectif" options={targets.map(g => ({ value: g.id, label: g.title }))} value={[chosen.id]} onValueChange={v => setGoalPick(v.at(-1) ?? chosen.id)} />}
            <BarChart label={`${chosen.title} : réalisé par semaine`} period={`${weekLabel(chain[0]?.w.start ?? week.start)} – ${fmtShort.format(parseDay(addDays(chain.at(-1)?.w.start ?? week.start, 6)))}`} data={bars} unit={chosen.many ?? ""} averageLabel="Moyenne" valueLabel="Réalisé" categoryLabel="Semaine" showAverage height={180} />
          </CardSurface>
        )}
      </div>
    </div>
  );
}

/** Monday of the week containing `iso`. */
const mondayOf = (iso: string) => addDays(iso, -((parseDay(iso).getDay() + 6) % 7));

const DRAG_TYPE = "application/x-tc-action";

/** One planning card. Desktop: the card opens a small Popover fiche anchored on it; mobile: the shared peek (BottomSheet). */
type KItem =
  | { kind: "action"; a: ActionItem; day: string | null }
  | { kind: "routine"; r: Routine; day: string | null };

function ActionFiche({ a, onOpenFull }: { a: ActionItem; onOpenFull: () => void }) {
  const s = useActivities();
  const done = !!s.actionDone[a.id];
  const goal = a.goal ? s.goals[a.goal] : undefined;
  return (
    <div className="act-fiche">
      <p className="act-fiche-title">{a.title}</p>
      <ActionDuePicker a={a} />
      {goal && <p className="act-fiche-meta"><Trophy {...META_ICON} /><span>{goal.title}</span></p>}
      <div className="act-fiche-actions">
        <Button size="sm" variant={done ? "secondary" : "primary"} aria-pressed={done} leading={done ? <Check size={16} aria-hidden /> : undefined} onClick={() => toggleAction(a.id, !done)}>{done ? "Terminé" : "Terminer"}</Button>
        <Button size="sm" variant="ghost" onClick={onOpenFull}>Ouvrir la fiche</Button>
      </div>
    </div>
  );
}

function RoutineFiche({ r, onOpenFull }: { r: Routine; onOpenFull: () => void }) {
  const s = useActivities();
  return (
    <div className="act-fiche">
      <p className="act-fiche-title">{r.title}</p>
      <p className="act-fiche-meta"><Repeat {...META_ICON} /><span>{r.rhythm}</span></p>
      {routineDueToday(r)
        ? <Checkbox label="Faite aujourd’hui" checked={!!s.routineToday[r.id]} onCheckedChange={c => toggleRoutine(r.id, c === true)} />
        : <p className="act-fiche-note">Prévue {r.short} : à cocher le jour même.</p>}
      <div className="act-fiche-actions"><Button size="sm" variant="ghost" onClick={onOpenFull}>Ouvrir la fiche</Button></div>
    </div>
  );
}

function KanbanCard({ item, mobile, openPeek, navigate }: { item: KItem; mobile: boolean; openPeek: OpenPeek; navigate: Navigate }) {
  const s = useActivities();
  const [open, setOpen] = useState(false);
  const isAction = item.kind === "action";
  const id = isAction ? item.a.id : item.r.id;
  const title = isAction ? item.a.title : item.r.title;
  const openFull = () => {
    setOpen(false);
    if (item.kind === "action") openPeek({ title, content: <ActionDetail id={id} navigate={navigate} />, footer: <ActionFooter id={id} /> });
    else openPeek({ title, content: <RoutineDetail id={id} />, footer: <RoutineFooter id={id} /> });
  };
  // Checkbox only where the store holds that state: an action (one done state), a routine on today (or free day = today's tick).
  // A routine on another day is read-only: a past day shows « Fait » when it is in `past`; no completion is faked.
  let done = false, toggle: ((c: boolean) => void) | undefined, meta: string | undefined;
  if (item.kind === "action") {
    done = !!s.actionDone[id]; toggle = c => toggleAction(id, c);
    meta = isActionLate(s, item.a) ? "En retard" : actionTime(item.a.meta);
  } else {
    const today = item.day === DEMO_TODAY || (item.day === null && routineDueToday(item.r));
    if (today) { done = !!s.routineToday[id]; toggle = c => toggleRoutine(id, c); }
    else if (item.day && item.r.past.includes(item.day)) { done = true; meta = "Fait"; }
    if (item.day === null) meta = item.r.short;
  }
  const body = <><span className="act-compact-title">{title}</span>{meta && <span className="act-compact-time">{meta}</span>}</>;
  return (
    <li className="act-compact-row act-kcard" data-done={done || undefined} data-kind={item.kind}
      draggable={isAction || undefined}
      onDragStart={isAction ? e => { e.dataTransfer.setData(DRAG_TYPE, id); e.dataTransfer.effectAllowed = "move"; } : undefined}>
      {toggle && <Checkbox aria-label={`${title} : faite`} checked={done} onCheckedChange={c => toggle?.(c === true)} />}
      {mobile
        ? <ButtonCore variant="ghost" accent={false} morph={false} className="act-compact-open" onClick={openFull}>{body}</ButtonCore>
        : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger className="act-compact-open act-kcard-open">{body}</PopoverTrigger>
            <PopoverContent className="act-fiche-pop" side="bottom" align="start" aria-label={title}>
              {item.kind === "action" ? <ActionFiche a={item.a} onOpenFull={openFull} /> : <RoutineFiche r={item.r} onOpenFull={openFull} />}
            </PopoverContent>
          </Popover>
        )}
    </li>
  );
}

type PlanningColumn = { key: string; day: string | null; title: string; items: KItem[] };

/** Current week starts with today's work and unfinished overdue work; other weeks retain their actual dated days. */
function planningColumns(s: ActivitiesState, start: string): PlanningColumn[] {
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const current = days.includes(DEMO_TODAY);
  const minutesOf = (it: KItem) => it.kind === "action" ? rowMinutes(actionTime(it.a.meta)) : Infinity;
  const itemsOf = (day: string | null): KItem[] => {
    const actions = s.actions.filter(a => {
      const due = effectiveDue(s, a);
      if (current && day === DEMO_TODAY) return due === day || (!s.actionDone[a.id] && ((due && due < day) || (!due && a.late)));
      if (day === null) return !due && !(current && a.late && !s.actionDone[a.id]);
      return due === day;
    }).map((a): KItem => ({ kind: "action", a, day }));
    const routines = (day === null ? s.routines.filter(r => r.days === null) : s.routines.filter(r => r.days?.includes(parseDay(day).getDay())))
      .map((r): KItem => ({ kind: "routine", r, day }));
    return [...actions, ...routines].sort((x, y) => minutesOf(x) - minutesOf(y));
  };
  const visibleDays = current ? days.filter(day => day >= DEMO_TODAY) : days;
  return [
    ...visibleDays.map(day => ({ key: day, day, title: day === DEMO_TODAY ? "Aujourd’hui · en retard" : fmtLong.format(parseDay(day)), items: itemsOf(day) })),
    { key: "none", day: null, title: "Sans date", items: itemsOf(null) },
  ];
}

/** One dated kanban, with genuine drag/date-picker assignments and no empty past columns ahead of today's work. */
function WeekPlanning({ openPeek, navigate }: { openPeek: OpenPeek; navigate: Navigate }) {
  const s = useActivities();
  const bilan = useCurrentBilan();
  const mobile = useMediaQuery(MOBILE_QUERY);
  const [offset, setOffset] = useState(0);
  const [over, setOver] = useState<string | null>(null);
  const start = addDays(mondayOf(DEMO_TODAY), offset * 7);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const columns = planningColumns(s, start);
  const drop = (day: string | null) => (e: DragEvent) => {
    const id = e.dataTransfer.getData(DRAG_TYPE);
    setOver(null);
    if (!id) return;
    e.preventDefault();
    setActionDue(id, day);
  };
  return (
    <Section title={`Semaine du ${fmtShort.format(parseDay(start))} au ${fmtShort.format(parseDay(days[6]!))}`} className="act-planning"
      aside={<div className="act-planning-nav">
        <Button variant="ghost" size="sm" aria-label="Semaine précédente" onClick={() => setOffset(o => o - 1)}><ChevronLeft size={16} aria-hidden /></Button>
        {offset !== 0 && <Button variant="ghost" size="sm" onClick={() => setOffset(0)}>Cette semaine</Button>}
        <Button variant="ghost" size="sm" aria-label="Semaine suivante" onClick={() => setOffset(o => o + 1)}><ChevronRight size={16} aria-hidden /></Button>
      </div>}>
      <div key={start} className="act-kanban" role="list" aria-label={`Planning ${weekLabel(start).toLowerCase()}`}>
        {columns.map(col => {
          const items = col.items;
          const hasBilan = col.day === DEMO_TODAY && bilan.open && !bilan.sent;
          return (
            <div key={col.key} role="listitem" className="act-kanban-col" data-today={col.day === DEMO_TODAY || undefined} data-undated={col.day === null || undefined}
              data-over={over === col.key || undefined}
              onDragOver={e => { if (e.dataTransfer.types.includes(DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== col.key) setOver(col.key); } }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(o => (o === col.key ? null : o)); }}
              onDrop={drop(col.day)}>
              <h3 className="act-kanban-h">{col.title}</h3>
              {hasBilan && <BilanTask route="agir" navigate={navigate} openPeek={openPeek} />}
              {items.length
                ? <ul className="act-compact-rows" aria-label={col.day ? dayLabel(col.day) : "Sans date"}>
                    {items.map(it => <KanbanCard key={`${it.kind}-${it.kind === "action" ? it.a.id : it.r.id}`} item={it} mobile={mobile} openPeek={openPeek} navigate={navigate} />)}
                  </ul>
                : !hasBilan && <p className="act-kanban-empty">Rien de prévu</p>}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

/** Bilans live in the Échanges store (existing BilansTab): opened in the shared peek, form and history included. */
const openBilans = (props: PageProps) => props.openPeek({ title: "Mes bilans", content: <BilanExperience {...props} /> });

/** Current bilan as a task, only while its proposal window is open and it is not sent (store values, real end date). */
function BilanTask(props: PageProps) {
  const b = useCurrentBilan();
  if (!b.open || b.sent) return null;
  return (
    <ul className="act-compact-rows act-bilan-task" aria-label="Bilan à faire">
      <li className="act-compact-row">
        <ButtonCore variant="ghost" accent={false} morph={false} className="act-compact-open" onClick={() => openBilans(props)}>
          <span className="act-compact-title">{b.title} à remplir</span>
          <span className="act-compact-time">jusqu’au {fmtShort.format(parseDay(b.to))}</span>
        </ButtonCore>
      </li>
    </ul>
  );
}

function AgirPage({ navigate, openPeek, route }: PageProps) {
  const s = useActivities();
  const [suivi, setSuivi] = useState(false);
  const [goalWeekKey, setGoalWeekKey] = useState(currentWeek(s).key);
  const week = s.weeks.find(w => w.key === goalWeekKey) ?? currentWeek(s);
  const weekIndex = s.weeks.findIndex(w => w.key === week.key);
  const continuous = Object.values(s.goals).filter(g => !g.week);
  const goals = week.goals.map(id => s.goals[id]).filter((g): g is Goal => !!g);
  if (suivi) return (
    <div className="act-page act-agir">
      <header className="act-agir-bar act-agir-bar-back">
        <Button variant="ghost" leading={<ArrowLeft size={16} />} onClick={() => setSuivi(false)}>Retour à Agir</Button>
        <h2 className="act-agir-week">Historique</h2>
      </header>
      <HistoryView openPeek={openPeek} navigate={navigate} />
      <Section title="Mes bilans">
        <div className="act-actions"><Button variant="secondary" leading={<FileText size={16} aria-hidden />} onClick={() => openBilans({ route, navigate, openPeek })}>Ouvrir mes bilans</Button></div>
      </Section>
      {continuous.length > 0 && (
        <Section title="Suivi au long cours">
          <div className="act-goal-grid">{continuous.map(g => <GoalCard key={g.id} g={g} openPeek={openPeek} navigate={navigate} />)}</div>
        </Section>
      )}
    </div>
  );
  return (
    <div className="act-page act-agir">
      {/* Agir: every goal of the week visible at once (shared card, no carousel); the Accueil keeps its swipe rail.
          The week label lives in the planning header only (no isolated week line at the top). */}
      <Section title={<span className="act-goal-week-heading"><span>Objectifs de la semaine</span><span className="act-goal-week-nav">
        <Button variant="ghost" size="sm" aria-label="Objectifs de la semaine précédente" disabled={weekIndex >= s.weeks.length - 1} onClick={() => setGoalWeekKey(s.weeks[weekIndex + 1]!.key)}><ChevronLeft size={16} /></Button>
        <span>{fmtShort.format(parseDay(week.start))} – {fmtShort.format(parseDay(addDays(week.start, 6)))}</span>
        <Button variant="ghost" size="sm" aria-label="Objectifs de la semaine suivante" disabled={weekIndex === 0} onClick={() => setGoalWeekKey(s.weeks[weekIndex - 1]!.key)}><ChevronRight size={16} /></Button>
      </span></span>} className="act-agir-objectives" aside={<div className="act-agir-tools">
        <Button variant="ghost" leading={<HistoryIcon size={16} aria-hidden />} onClick={() => setSuivi(true)}>Historique</Button>
        <Button variant="secondary" className="act-add-enabled" leading={<Plus size={16} />} onClick={() => openAdd(openPeek)}>Ajouter</Button>
      </div>}>
        {goals.length === 0
          ? <EmptyState icon={false} title="Quel est ton cap cette semaine ?" action={<Button onClick={() => openAdd(openPeek, "goal")}>Écrire mes objectifs</Button>} />
          : <div className="act-agir-goals"><div className="act-agir-goal-grid">{goals.map(g => <GoalCard key={g.id} g={g} openPeek={openPeek} navigate={navigate} />)}</div></div>}
      </Section>
      <WeekPlanning openPeek={openPeek} navigate={navigate} />
    </div>
  );
}

// ── Apprendre ────────────────────────────────────────────────────────────────

const stepComplete = (s: ActivitiesState, stepId: string) => (STEPS[stepId]?.lessons ?? []).every(id => LESSONS[id]?.soon || s.learn.done[id]);
const stepLocked = (s: ActivitiesState, stepId: string) => { const p = STEPS[stepId]?.prereq; return !!p && !stepComplete(s, p); };
const fmtClock = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
const formatLabel = (f: Lesson["format"]) => (f === "video" ? "Vidéo" : f === "text" ? "Texte" : f === "file" ? "Fiche" : f === "support" ? "Support écrit" : "Outil");

function Reader({ item }: { item: Lesson | Resource }) {
  const s = useActivities();
  const [opened, setOpened] = useState("");
  const isLesson = "step" in item;
  const lesson = isLesson ? (item as Lesson) : undefined;
  const resource = isLesson ? undefined : (item as Resource);
  if (lesson?.soon) return <EmptyState icon={false} title="Bientôt disponible" description="Cette séance sera ajoutée à l’étape prochainement." />;
  if (lesson && stepLocked(s, lesson.step)) {
    const p = STEPS[STEPS[lesson.step]!.prereq!]!;
    return <EmptyState icon={<Lock size={22} />} title="Étape verrouillée" description={`Termine d’abord l’étape ${p.n} · ${p.title}.`} />;
  }
  const done = lesson ? !!s.learn.done[lesson.id] : false;
  const pos = lesson ? s.learn.pos[lesson.id] : undefined;
  const cover = lesson?.cover ? LESSON_COVERS[lesson.cover] : undefined;
  return (
    <div className="act-peek act-reader">
      {cover && <img className="act-reader-cover" src={cover} alt="" />}
      <div className="act-badges">
        <Badge size="sm">{formatLabel(item.format)}</Badge>
        <Badge size="sm" tone="info">{item.theme}</Badge>
        {lesson?.dur && <Badge size="sm">{lesson.dur}</Badge>}
        {resource && <Badge size="sm">{resource.meta}</Badge>}
        {done && <Badge size="sm" tone="success">Terminée</Badge>}
      </div>
      {item.summary && <p className="act-text act-lead">{item.summary}</p>}
      {item.format === "video" && item.durSec !== undefined && (
        <div className="act-video">
          <PlayCircle size={28} aria-hidden />
          <Progress value={pos ?? (done ? item.durSec : 0)} max={item.durSec} label={pos ? `Reprise à ${fmtClock(pos)} sur ${fmtClock(item.durSec)}` : `Durée ${fmtClock(item.durSec)}`} />        </div>
      )}
      {item.excerpt?.map(p => <p key={p} className="act-text">{p}</p>)}
      {resource?.parts?.map(p => <section key={p.h} className="act-reader-part"><h3 className="act-peek-h">{p.h}</h3><p className="act-text">{p.t}</p></section>)}
      {resource?.replayUnavailable && <Notice>Replay non disponible : le support écrit reste consultable.</Notice>}
      {item.files && item.files.length > 0 && (
        <ul className="act-rows" aria-label="Fichiers">
          {item.files.map(f => (
            <li key={f.name} className="act-row">
              <span className="act-row-main"><span><FileText size={14} aria-hidden /> {f.name}</span><small>{f.meta}</small></span>
              <Button variant="secondary" size="sm" disabled>Télécharger</Button>
            </li>
          ))}
        </ul>
      )}      {resource?.ext && (<>
        <Button variant="secondary" trailing={<ExternalLink size={16} />} onClick={() => setOpened("opened")}>Ouvrir l’outil</Button>
      </>)}
      {lesson && <Checkbox label="Séance terminée" description={done ? `Terminée le ${dayLabel(s.learn.done[lesson.id]!)}` : "Marque-la quand tu l’as suivie."} checked={done} onCheckedChange={c => markLesson(lesson.id, c === true)} />}
    </div>
  );
}
const openReader = (openPeek: OpenPeek, item: Lesson | Resource) => openPeek({ title: item.title, content: <Reader item={item} /> });

function LibraryFilters() {
  const s = useActivities();
  const themes = Array.from(new Set(RESOURCES.map(r => r.theme)));
  return (
    <div className="act-peek">
      <ChipGroup label="Thèmes" multiple options={themes.map(t => ({ value: t, label: t }))} value={s.libraryThemes} onValueChange={v => update(st => ({ ...st, libraryThemes: v }))} />
      <Button variant="ghost" disabled={s.libraryThemes.length === 0} onClick={() => update(st => ({ ...st, libraryThemes: [] }))}>Effacer les filtres</Button>
    </div>
  );
}

function LessonCard({ l, openPeek }: { l: Lesson; openPeek: OpenPeek }) {
  const s = useActivities();
  const locked = stepLocked(s, l.step);
  const status = l.soon ? "Bientôt" : locked ? "Verrouillée" : s.learn.done[l.id] ? "Terminée" : s.learn.pos[l.id] ? "En cours" : undefined;
  return (
    <Card title={l.title} description={l.summary} meta={[formatLabel(l.format), l.dur].filter(Boolean).join(" · ")} status={status}
      media={l.cover ? <img src={LESSON_COVERS[l.cover]} alt="" className="act-card-media" /> : undefined}
      action={<Button size="sm" variant="secondary" disabled={l.soon} onClick={() => openReader(openPeek, l)}>{locked ? "Voir" : s.learn.pos[l.id] ? "Reprendre" : "Ouvrir"}</Button>} />
  );
}

function LearnPage({ navigate, openPeek }: PageProps) {
  const s = useActivities();
  const [levelIndex, setLevelIndex] = useState(0);
  const [stepId, setStepId] = useState("E2");
  const level = LEVELS[levelIndex] ?? LEVELS[0]!;
  const last = LESSONS[s.learn.last];
  const lastPos = last ? s.learn.pos[last.id] : undefined;
  const levelLessons = (lv: typeof level) => lv.steps.flatMap(id => STEPS[id]?.lessons ?? []).filter(id => !LESSONS[id]?.soon);
  const nodes: TreeNode[] = level.steps.map(id => STEPS[id]!).map(st => ({
    id: st.id, label: `Étape ${st.n} · ${st.title}${stepLocked(s, st.id) ? " · verrouillée" : ""}`, icon: stepLocked(s, st.id) ? <Lock size={14} /> : <BookOpen size={14} />,
    children: st.lessons.map(id => LESSONS[id]!).map(l => ({ id: l.id, label: `${l.title}${l.soon ? " · bientôt" : s.learn.done[l.id] ? " · terminée" : ""}` })),
  }));
  const step = STEPS[stepId] && STEPS[stepId]!.level === level.id ? STEPS[stepId]! : STEPS[level.steps[0] ?? ""];
  const library = RESOURCES.filter(r => (s.libraryColl === "all" || r.coll === s.libraryColl) && (s.libraryThemes.length === 0 || s.libraryThemes.includes(r.theme)));
  const accessTone: Record<string, BadgeTone> = { included: "success", not_included: "warning", soon: "neutral" };
  const accessLabel: Record<string, string> = { included: "Inclus", not_included: "Non inclus", soon: "Bientôt" };
  return (
    <div className="act-page act-learn">
      {last && (
        <CardSurface className="act-resume" padding="md">
          {last.cover && <img src={LESSON_COVERS[last.cover]} alt="" />}
          <div className="act-resume-body">
            <p className="act-peek-kicker">Reprendre · Étape {STEPS[last.step]?.n} · {STEPS[last.step]?.title}</p>
            <h2>{last.title}</h2>
            {lastPos !== undefined && last.durSec !== undefined && <Progress value={lastPos} max={last.durSec} label={`Reprise à ${fmtClock(lastPos)} sur ${fmtClock(last.durSec)}`} />}
            <Button onClick={() => openReader(openPeek, last)}>Reprendre</Button>
          </div>
        </CardSurface>
      )}

      <Section title="Mon parcours">
        <Carousel label="Niveaux" index={levelIndex} onIndexChange={setLevelIndex} slideSize="min(78cqw, 320px)" slideLabel={(i, n) => `Niveau ${i + 1} sur ${n}`}>
          {LEVELS.map(lv => {
            const ids = levelLessons(lv), done = ids.filter(id => s.learn.done[id]).length;
            return (
              <CardSurface key={lv.id} className="act-level" padding="md" data-current={lv.id === level.id || undefined}>
                <div className="act-goal-card-head"><h3>{lv.title}</h3><Badge size="sm" tone={accessTone[lv.access]}>{accessLabel[lv.access]}</Badge></div>
                <p className="act-muted">{lv.subtitle}</p>
                {lv.access === "included" ? <Progress value={done} max={Math.max(ids.length, 1)} label={`${done} séances sur ${ids.length}`} showValue />
                  : <p className="act-muted">{lv.stepCount ? `${lv.stepCount} étapes` : "Contenu en préparation"}</p>}
              </CardSurface>
            );
          })}
        </Carousel>
      </Section>

      {level.access === "not_included" && (
        <EmptyState icon={<Lock size={22} />} title={`${level.title} non inclus`} description={`${level.subtitle} : ce niveau ne fait pas partie de ton accompagnement actuel.`}
          action={<Button variant="secondary" onClick={() => navigate("shop")}>Voir les offres</Button>} />
      )}
      {level.access === "soon" && <EmptyState icon={false} title={`${level.title} bientôt disponible`} description={level.subtitle} />}

      {level.access === "included" && step && (
        <div className="act-learn-path">
          <CardSurface padding="sm" className="act-tree">
            <TreeView aria-label={`Étapes du ${level.title}`} nodes={nodes} defaultExpandedIds={[stepId]} selectedId={step.id}
              onSelect={node => {
                if (STEPS[node.id]) { setStepId(node.id); return; }
                const l = LESSONS[node.id]; if (l) { setStepId(l.step); openReader(openPeek, l); }
              }} />
          </CardSurface>
          <Section title={`Étape ${step.n} · ${step.title}`} aside={stepLocked(s, step.id) ? <Badge size="sm" tone="warning" icon={<Lock size={12} />}>Verrouillée</Badge> : null}>
            <div className="act-card-grid">{step.lessons.map(id => LESSONS[id]!).map(l => <LessonCard key={l.id} l={l} openPeek={openPeek} />)}</div>
          </Section>
        </div>
      )}

      <Section title="Bibliothèque" aside={
        <Button variant="secondary" size="sm" leading={<SlidersHorizontal size={16} />} onClick={() => openPeek({ title: "Filtres", content: <LibraryFilters /> })}>
          Filtres{s.libraryThemes.length ? ` · ${s.libraryThemes.length}` : ""}
        </Button>}>
        <SegmentedControl label="Collection" options={RESOURCE_COLLECTIONS} value={s.libraryColl} onValueChange={v => update(st => ({ ...st, libraryColl: v as ActivitiesState["libraryColl"] }))} />
        {library.length === 0 ? (
          <EmptyState icon={false} title="Aucun contenu pour ces filtres" action={<Button variant="secondary" onClick={() => update(st => ({ ...st, libraryThemes: [], libraryColl: "all" }))}>Effacer les filtres</Button>} />
        ) : (
          <div className="act-card-grid">
            {library.map(r => (
              <Card key={r.id} title={r.title} description={r.summary} meta={`${r.theme} · ${r.meta}`}
                action={<Button size="sm" variant="secondary" trailing={r.ext ? <ExternalLink size={14} /> : undefined} onClick={() => openReader(openPeek, r)}>{r.ext ? "Ouvrir l’outil" : "Ouvrir"}</Button>} />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ── Liberty Coach ────────────────────────────────────────────────────────────

/** One shared Liberty conversation: point preparation and document requests remain actionable without a fabricated URL. */
function sendLibertyMessage(text: string) {
  const clean = text.trim();
  if (!clean) return false;
  const id = nextId("lm");
  update(s => ({ ...s, libertyMessages: [...s.libertyMessages, { id, authorId: "me", text: clean,
    createdAt: DEMO_NOW.getTime() + s.libertyMessages.length * 60000, status: "sent" }] }));
  return true;
}

function LibertyMessageForm({ initial }: { initial: string }) {
  const [text, setText] = useState(initial);
  const [sent, setSent] = useState(false);
  return <form className="act-form" onSubmit={e => { e.preventDefault(); if (sendLibertyMessage(text)) setSent(true); }}>
    <Textarea label="Ton message à l’équipe" rows={4} value={text} onChange={e => { setText(e.target.value); setSent(false); }} />
    <Button type="submit" disabled={!text.trim() || sent}>{sent ? "Demande ajoutée à la conversation" : "Envoyer ma demande"}</Button>
  </form>;
}

function LibertyPointForm() {
  const s = useActivities();
  const [focus, setFocus] = useState(s.libertyPoint?.focus ?? "");
  const [results, setResults] = useState(s.libertyPoint?.results ?? "");
  const [question, setQuestion] = useState(s.libertyPoint?.question ?? "");
  const [saved, setSaved] = useState(false);
  return <form className="act-form" onSubmit={e => { e.preventDefault(); if (!focus.trim()) return;
    update(st => ({ ...st, libertyPoint: { focus: focus.trim(), results: results.trim(), question: question.trim() } })); setSaved(true); }}>
    <Input label="Mon cap pour la semaine" value={focus} onChange={e => { setFocus(e.target.value); setSaved(false); }} placeholder="Ce que je veux faire avancer" />
    <Textarea label="Mes résultats et observations" rows={3} value={results} onChange={e => { setResults(e.target.value); setSaved(false); }} />
    <Textarea label="Ma question pour l’équipe" rows={2} value={question} onChange={e => { setQuestion(e.target.value); setSaved(false); }} />
    <Button type="submit" disabled={!focus.trim()}>{saved ? "Point enregistré" : "Enregistrer mon point"}</Button>
  </form>;
}

function LibertyActivity() {
  const s = useActivities();
  const week = currentWeek(s);
  const goals = week.goals.map(id => s.goals[id]).filter((g): g is Goal => !!g);
  return <div className="act-peek">
    <p className="act-peek-kicker">{weekLabel(week.start)}</p>
    <div className="act-liberty-results">{goals.map(g => { const m = measureOf(s, g); return <CardSurface key={g.id} padding="md">
      <p className="act-text">{g.title}</p><div className="act-measure"><b>{m.big}</b><span>{m.small}</span></div>
      {m.target !== undefined && <Progress value={m.pct} max={100} aria-label={g.title} />}
    </CardSurface>; })}</div>
    {s.libertyPoint && <MetaList items={[
      { icon: <Trophy {...META_ICON} />, label: "Mon cap", value: s.libertyPoint.focus },
      s.libertyPoint.results && { icon: <ListTodo {...META_ICON} />, label: "Mon point", value: s.libertyPoint.results },
      s.libertyPoint.question && { icon: <MessageCircle {...META_ICON} />, label: "Ma question", value: s.libertyPoint.question },
    ]} />}
  </div>;
}

/** Vente has a three-step working journey; the other categories offer a conversation, without invented offers. */
function LibertyPage({ navigate, openPeek }: PageProps) {
  const inside = useSyncExternalStore(listener => { window.addEventListener("hashchange",listener); window.addEventListener("popstate",listener); return ()=>{window.removeEventListener("hashchange",listener);window.removeEventListener("popstate",listener);}; },()=>new URLSearchParams(window.location.hash.split("?")[1]??"").get("view")==="vente",()=>false);
  const covers: Record<string,string> = {vente:"./assets/tc-liberty-vente-0810.png",sport:"./assets/tc-liberty-sport-0810.png",compta:"./assets/tc-liberty-compta-0810.png",social:"./assets/tc-liberty-social-0810.png"};
  const openDocument = (item: LibertyLink) => openPeek({title:item.title,content:<div className="act-liberty-document"><p className="act-muted">{item.kind}</p>{item.id === "suivi" || item.id === "modele" ? <LibertyActivity/> : <><h3>{item.title}</h3><p>Note tes actions commerciales, tes résultats et les questions à travailler avec ton équipe. Prépare ton point chaque semaine pour décider de la prochaine action.</p><Button onClick={()=>navigate("team")}>Écrire à mon équipe</Button></>}</div>});
  if (!inside) return <div className="act-page"><div className="act-liberty-entry-grid">{LIBERTY_CATEGORIES.map(c=><CardSurface key={c.id} asChild padding="none" className="act-liberty-entry-card"><ButtonCore variant="ghost" accent={false} morph={false} className="lrn-level" disabled={c.id!=="vente"} onClick={()=>{window.location.hash="/liberty?view=vente";}} aria-label={`${c.label}${c.id!=="vente"?", bientôt disponible":". Ouvrir Liberty Coach Vente"}`}><span className="act-liberty-entry-media"><img src={covers[c.id]} alt=""/>{c.id!=="vente"&&<span className="act-liberty-entry-lock"><Lock size={25}/></span>}</span><span className="act-liberty-entry-body"><b>{c.label}</b><span>{c.id==="vente"?"Mon accompagnement commercial":"Bientôt disponible"}</span></span></ButtonCore></CardSurface>)}</div></div>;
  return <div className="act-page act-liberty-sale">
    
    <div className="act-liberty-sale-grid"><section className="act-liberty-sale-main"><Section title="Mes documents"><div className="act-liberty-documents">{LIBERTY_LINKS.map(item=><Card key={item.id} className="act-liberty-file-card" title={item.title} description={item.kind} meta={<FileText size={22}/>} onClick={()=>item.set?openDocument(item):openPeek({title:item.title,content:<LibertyMessageForm initial="Bonjour, peux-tu me transmettre mon relevé de commissions ?"/>})}/>)}</div></Section></section>
    <aside className="act-liberty-sale-aside"><Section title="Ressources"><div className="act-liberty-documents">{LIBERTY_RESOURCES.map(item=><Card key={item.id} className="act-liberty-file-card" title={item.title} description={item.kind} meta={<FileText size={20}/>} onClick={()=>openDocument(item)}/>)}</div></Section><Button variant="ghost" leading={<MessageCircle size={17}/>} onClick={()=>navigate("team")}>Mon équipe Liberty Coach</Button></aside></div>
  </div>;
}

// ── Point d’entrée du domaine ────────────────────────────────────────────────

export function ActivitiesPages(props: PageProps) {
  switch (props.route) {
    case "home": return <HomePage {...props} />;
    case "agir": return <AgirPage {...props} />;
    case "learn": return <LearnPage {...props} />;
    case "liberty": return <LibertyPage {...props} />;
    default: return null;
  }
}

export default ActivitiesPages;
